import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AccountProfile } from '@mypoz/shared';
import { normaliseAccountProfile } from '@mypoz/shared';

import { AccountContext, type AccountState } from './context.js';
import { fetchAccountRecord } from './api.js';
import { withDevVerification } from './verification.js';

/**
 * The session: the account profile, fetched once for the whole app (MYP-73, MYP-105).
 *
 * It sits above the Wallet provider, which seeds its balance, currency and payment mode from this
 * profile, so nothing reads the account from anywhere but here. There is no fixture behind it: until
 * the record arrives, and if it never does, `profile` is undefined and the screens say so
 * (`SessionGate`) rather than showing a customer nobody is.
 *
 * Only the allow-listed profile is held. The raw `alipay_page2` record — 99 fields — exists only
 * inside the promise callback below, is normalised there, and is dropped; it is never put in state.
 * Credentials are already gone before it gets that far (`stripCredentials`, in the transport).
 *
 * View-only (MYP-72, revised per mentor feedback) — there is no `updateProfile` and no editing
 * anywhere. Wiring changes where the values come from, not what the screen lets you do with them.
 */
export function AccountProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<AccountProfile | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;

    setProfile(undefined);
    setFailed(false);

    fetchAccountRecord().then(
      (record) => {
        if (!current) return;
        const next = normaliseAccountProfile(record);
        if (next) {
          setProfile(withDevVerification(next));
        } else {
          // A record that arrived but could not be read is an error, not a success.
          console.error('[account] the account record could not be read');
          setFailed(true);
        }
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[account] could not load the account record', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const refresh = useCallback(async () => {
    try {
      const next = normaliseAccountProfile(await fetchAccountRecord());
      if (next) {
        setProfile(withDevVerification(next));
        return true;
      }
      console.error('[account] the refreshed account record could not be read');
    } catch (cause) {
      console.error('[account] could not refresh the account record', cause);
    }
    return false;
  }, []);

  const value = useMemo<AccountState>(
    () => ({
      profile,
      status: profile ? 'ready' : failed ? 'error' : 'loading',
      reload,
      refresh,
    }),
    [profile, failed, reload, refresh],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
